import axios from 'axios';
import { BASE_URL } from '../apiConfig';
import { getTenantHeaders } from './tenant';

export type TeacherQaChannel = {
  url: string;
  label: string;
};

const AMIN_CHANNEL: TeacherQaChannel = {
  url: 'https://t.me/teameldamelalmemramin',
  label: 'فريق الأستاذ أمين',
};

const SAEED_CHANNEL: TeacherQaChannel = {
  url: 'https://t.me/teamelmydrsaeedelfiky',
  label: 'فريق الأستاذ سعيد الفقي',
};

const TEACHER_QA_CHANNELS: Record<string, TeacherQaChannel[]> = {
  '868fc5b5-5381-4450-a55c-5b0cb167c748': [AMIN_CHANNEL],
  '25f2257d-1ed6-4d55-bec4-33aefdafc351': [SAEED_CHANNEL],
  '8fb01e1d-ca7d-4bb2-956b-37bae6e9edb3': [AMIN_CHANNEL, SAEED_CHANNEL],
};

const QA_TEACHER_IDS = Object.keys(TEACHER_QA_CHANNELS);
const teacherIdsByCourse = new Map<string, string[]>();

export function extractCourseTeacherIds(course: unknown): string[] {
  if (!course || typeof course !== 'object') return [];
  const raw = course as Record<string, unknown>;
  const ids: string[] = [];
  const add = (value: unknown) => {
    const id = String(value ?? '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  };

  add(raw.teacherId ?? raw.TeacherId);

  const extraIds = raw.teacherIds ?? raw.TeacherIds;
  if (Array.isArray(extraIds)) {
    extraIds.forEach(add);
  } else if (typeof extraIds === 'string') {
    extraIds.split(/[,\s]+/).forEach(add);
  }

  const nestedTeacher = raw.teacher ?? raw.Teacher;
  if (nestedTeacher && typeof nestedTeacher === 'object') {
    const item = nestedTeacher as { id?: string; Id?: string };
    add(item.id ?? item.Id);
  }

  const teachers = raw.teachers ?? raw.Teachers;
  if (Array.isArray(teachers)) {
    teachers.forEach((teacher) => {
      if (teacher && typeof teacher === 'object') {
        const item = teacher as { id?: string; Id?: string };
        add(item.id ?? item.Id);
      } else {
        add(teacher);
      }
    });
  }

  return ids;
}

function extractCoursesList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  if (Array.isArray(raw.data)) return raw.data as Record<string, unknown>[];
  if (Array.isArray(raw.courses)) return raw.courses as Record<string, unknown>[];
  if (Array.isArray(raw.Courses)) return raw.Courses as Record<string, unknown>[];
  return [];
}

interface CourseAllPayload {
  totalCount?: number;
  totalPages?: number;
  TotalPages?: number;
  data?: unknown;
  courses?: unknown;
  Courses?: unknown;
}

interface CourseAllResponse {
  success?: boolean;
  data?: CourseAllPayload | Record<string, unknown>[];
}

async function teacherOwnsCourse(teacherId: string, courseId: string): Promise<boolean> {
  const pageSize = 50;
  let page = 1;
  let totalPages = 1;

  do {
    const response = await axios.get<CourseAllResponse>(`${BASE_URL}api/Course/all`, {
      params: {
        pageNumber: page,
        pageSize,
        teacherid: teacherId,
        active: true,
      },
      headers: getTenantHeaders(),
    });

    const payload = response.data.data;
    const courses = extractCoursesList(payload);
    const found = courses.some((course) => String(course.id ?? course.Id ?? '') === courseId);
    if (found) return true;

    const raw = payload && !Array.isArray(payload) ? payload : {};
    totalPages = Number(raw.totalPages ?? raw.TotalPages ?? 1) || 1;
    page += 1;
  } while (page <= totalPages);

  return false;
}

export async function resolveCourseTeacherIds(
  courseId: string,
  coursePayload?: unknown
): Promise<string[]> {
  const fromPayload = extractCourseTeacherIds(coursePayload);
  if (fromPayload.some((id) => TEACHER_QA_CHANNELS[id])) {
    teacherIdsByCourse.set(courseId, fromPayload);
    return fromPayload;
  }

  const cached = teacherIdsByCourse.get(courseId);
  if (cached) return cached;

  const matched = (
    await Promise.all(
      QA_TEACHER_IDS.map(async (teacherId) => {
        try {
          return (await teacherOwnsCourse(teacherId, courseId)) ? teacherId : null;
        } catch {
          return null;
        }
      })
    )
  ).filter((id): id is string => Boolean(id));

  const resolved = matched.length > 0 ? matched : fromPayload;
  teacherIdsByCourse.set(courseId, resolved);
  return resolved;
}

export function getTeacherQaChannels(teacherIds: string[]): TeacherQaChannel[] {
  const channels: TeacherQaChannel[] = [];
  const seen = new Set<string>();

  teacherIds.forEach((teacherId) => {
    const mapped = TEACHER_QA_CHANNELS[teacherId];
    if (!mapped) return;
    mapped.forEach((channel) => {
      if (seen.has(channel.url)) return;
      seen.add(channel.url);
      channels.push(channel);
    });
  });

  return channels;
}

export async function resolveTeacherQaChannels(
  courseId: string,
  coursePayload?: unknown
): Promise<TeacherQaChannel[]> {
  const teacherIds = await resolveCourseTeacherIds(courseId, coursePayload);
  return getTeacherQaChannels(teacherIds);
}

export function shouldRenderTeacherQaNotice(course: unknown): boolean {
  const teacherIds = extractCourseTeacherIds(course);
  if (teacherIds.length === 0) return true;
  return getTeacherQaChannels(teacherIds).length > 0;
}
